import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Calendar, Code, Layers } from 'lucide-react';
import { FormulaScopeHint } from '@/components/semantic-models/FormulaScopeHint';
import { BucketEditor } from '@/components/semantic-models/BucketEditor';

// Quick date-part transforms — produce a formula like month(book_date).
const DATE_PARTS = [
  { fn: 'year', label: 'Year', sample: '2024' },
  { fn: 'quarter', label: 'Quarter', sample: 'Q1' },
  { fn: 'month', label: 'Month', sample: 'January' },
  { fn: 'monthnum', label: 'Month #', sample: '01' },
  { fn: 'ym', label: 'Year-Month', sample: '2024-01' },
  { fn: 'weekday', label: 'Weekday', sample: 'Monday' },
  { fn: 'day', label: 'Day', sample: '15' },
];

/**
 * Add Calculated Dimension dialog. Lets users derive a dimension column either
 * via quick date-part buttons (e.g. Month of book_date -> "Book Month") or a
 * free-form advanced expression, with an optional sort-by field.
 */
export function CalcDimensionDialog({ open, onOpenChange, selectedEntityData, onCreate, modelId }) {
  const [name, setName] = useState('');
  const [formula, setFormula] = useState('');
  const [sortField, setSortField] = useState('__none');
  const [mode, setMode] = useState('quick'); // 'quick' | 'buckets' | 'advanced'
  const [baseField, setBaseField] = useState('');
  const [bucketSpec, setBucketSpec] = useState(null);
  const [bucketSortExpr, setBucketSortExpr] = useState('');
  const [bucketValid, setBucketValid] = useState(false);

  const attrs = selectedEntityData?.attributes || [];
  const dateFields = attrs.filter(a => (a.data_type || '').toLowerCase().includes('date'));
  const numericFields = attrs.filter(a => ['integer', 'float', 'number'].includes((a.data_type || '').toLowerCase()) || a.semantic_type === 'measure');
  const textFields = attrs.filter(a => ['string', 'text', 'varchar', 'character'].includes((a.data_type || '').toLowerCase()));

  const reset = () => { setName(''); setFormula(''); setSortField('__none'); setMode('quick'); setBaseField(''); setBucketSpec(null); setBucketValid(false); setBucketSortExpr(''); };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const applyDatePart = (fn) => {
    if (!baseField) return;
    const fx = `${fn}(${baseField})`;
    setFormula(fx);
    if (!name.trim()) {
      const part = DATE_PARTS.find(d => d.fn === fn)?.label || fn;
      const pretty = baseField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      setName(`${pretty} ${part}`);
    }
  };

  const create = () => {
    if (!name.trim()) return;
    if (mode === 'buckets') {
      if (!bucketValid || !formula.trim()) return;
      onCreate({
        name: name.replace(/\s+/g, '_').toLowerCase(),
        display_name: name,
        formula: formula.trim(),
        subtype: 'bucket',
        bucket_spec: bucketSpec,
        sort_expr: bucketSortExpr,
        sort_field: null,
      });
      close(false);
      return;
    }
    if (!formula.trim()) return;
    onCreate({
      name: name.replace(/\s+/g, '_').toLowerCase(),
      display_name: name,
      formula: formula.trim(),
      sort_field: sortField === '__none' ? null : sortField,
    });
    close(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="calc-dimension-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add Calculated Dimension</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 -mt-1 mb-1">
          <Badge className="text-[9px] bg-blue-500/10 text-blue-300 border-blue-500/15">Dimension Column</Badge>
          <span className="text-[10px] text-muted-foreground">Belongs to {selectedEntityData?.name || 'selected entity'}</span>
        </div>

        <div className="space-y-3 mt-1 flex-1 overflow-auto">
          <div>
            <Label className="text-[10px] text-muted-foreground">Dimension Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Book Month"
              className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground" data-testid="calc-dim-name" />
          </div>

          <div className="flex gap-1 p-0.5 bg-muted/40 rounded-lg w-fit">
            <button onClick={() => setMode('quick')} className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11px] ${mode === 'quick' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="calc-dim-mode-quick"><Calendar className="w-3 h-3" />Date part</button>
            <button onClick={() => setMode('buckets')} className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11px] ${mode === 'buckets' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="calc-dim-mode-buckets"><Layers className="w-3 h-3" />Bins & Groups</button>
            <button onClick={() => setMode('advanced')} className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11px] ${mode === 'advanced' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid="calc-dim-mode-advanced"><Code className="w-3 h-3" />Advanced</button>
          </div>

          {mode === 'quick' && (
            <div className="space-y-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Date field</Label>
                <Select value={baseField} onValueChange={setBaseField}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 h-8 text-sm" data-testid="calc-dim-base-field"><SelectValue placeholder="Choose a date field" /></SelectTrigger>
                  <SelectContent>
                    {dateFields.length === 0 && <SelectItem value="__nodate" disabled>No date fields in this entity</SelectItem>}
                    {dateFields.map(f => <SelectItem key={f.name} value={f.name}>{f.display_name || f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {DATE_PARTS.map(d => (
                  <button key={d.fn} onClick={() => applyDatePart(d.fn)} disabled={!baseField}
                    className="px-2.5 py-1 rounded-lg border border-border/50 text-[11px] text-foreground hover:bg-primary/10 hover:border-primary/40 disabled:opacity-40 transition-colors"
                    data-testid={`calc-dim-part-${d.fn}`} title={`e.g. ${d.sample}`}>{d.label}</button>
                ))}
              </div>
            </div>
          )}

          {mode === 'buckets' && (
            <BucketEditor
              modelId={modelId}
              entityName={selectedEntityData?.name}
              fields={attrs}
              fieldsNumeric={numericFields}
              fieldsDate={dateFields}
              fieldsText={textFields}
              testIdPrefix="calc-dim-bucket"
              onChange={({ spec, valid, formula: fx, sortExpr }) => {
                setBucketSpec(spec);
                setBucketValid(valid);
                if (valid) { setFormula(fx || ''); setBucketSortExpr(sortExpr || ''); }
                else       { setFormula(''); setBucketSortExpr(''); }
              }}
            />
          )}

          {mode === 'advanced' && (
            <div>
              <Label className="text-[10px] text-muted-foreground">Expression</Label>
              <Input value={formula} onChange={e => setFormula(e.target.value)} placeholder="e.g. month(book_date), quarter(book_date), upper(state)"
                className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm font-mono text-foreground" data-testid="calc-dim-formula" />
              <p className="text-[9px] text-muted-foreground/70 mt-1">Supported: year, quarter, month, monthnum, ym, weekday, day, week, date, upper, lower — applied to a field, e.g. <code>month(book_date)</code>.</p>
            </div>
          )}

          {mode !== 'buckets' && formula && <div className="text-[10px] text-muted-foreground">Formula: <code className="text-primary">{formula}</code></div>}
          {mode !== 'buckets' && <FormulaScopeHint modelId={modelId} formula={formula} />}

          {mode !== 'buckets' && (
            <div>
              <Label className="text-[10px] text-muted-foreground">Sort by (optional)</Label>
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 h-8 text-sm" data-testid="calc-dim-sort-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Alphabetical (default)</SelectItem>
                  {attrs.map(a => <SelectItem key={a.name} value={a.name}>Sort by {a.display_name || a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground/70 mt-1">Choose a field to control ordering (e.g. sort "Book Month" by book_date for chronological order).</p>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-border/30 mt-2">
          <Button onClick={create} disabled={!name.trim() || (mode === 'buckets' ? !bucketValid : !formula.trim())}
            className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="confirm-calc-dimension">
            <Plus className="w-4 h-4 mr-2" />
            {mode === 'buckets' ? 'Create Grouped Dimension' : 'Create Calculated Dimension'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
